import { generateCodeVerifier, sha256, generateStateChallenge } from './pkce-utils';
import * as microsoftTeams from "@microsoft/teams-js";

// SSO Debug logging
const ssoDebugLog = [];
const logSso = (step, message, data = null) => {
  const entry = {
    timestamp: new Date().toISOString(),
    step,
    message,
    data: data ? JSON.stringify(data, null, 2) : null
  };
  ssoDebugLog.push(entry);
  console.log(`[SSO ${step}] ${message}`, data || '');
};

// Export debug log for UI display
export const getSsoDebugLog = () => [...ssoDebugLog];
export const clearSsoDebugLog = () => { ssoDebugLog.length = 0; };

// Environment configuration
const config = {
  clientId: process.env.VUE_APP_CLIENT_ID || '__VUE_APP_CLIENT_ID__',
  tenantId: process.env.VUE_APP_TENANT_ID || '__VUE_APP_TENANT_ID__',
  apiUrl: process.env.VUE_APP_API_URL || '__VUE_APP_API_URL__',
  frontendUrl: process.env.VUE_APP_FRONTEND_URL || window.location.origin,
  apiScopeUri: process.env.VUE_APP_API_SCOPE_URI || null, // e.g. api://contacts.example.com/{clientId}
};

// Branding configuration (exported for use in components)
export const branding = {
  appTitle: process.env.VUE_APP_APP_TITLE || 'prime Directory',
  companyName: 'core.two Holding GmbH',
  logoLight: '/core-two-logo-light.svg',
  logoDark: '/core-two-logo-dark.svg',
  storagePrefix: process.env.VUE_APP_STORAGE_PREFIX || 'primedirectory',
};

// Teams detection
let isInTeams = false;
let teamsContext = null;

/**
 * Initialize Teams SDK and attempt SSO
 * @returns {Promise<boolean>} - true if SSO succeeded, false if manual login needed
 */
export const initializeTeams = async () => {
  logSso('INIT', 'Starting Teams SDK initialization...');
  
  try {
    // Check URL parameters for Teams indicators
    const urlParams = new URLSearchParams(window.location.search);
    logSso('INIT', 'URL parameters', {
      inTeams: urlParams.get('inTeams'),
      fullUrl: window.location.href
    });
    
    await microsoftTeams.app.initialize();
    isInTeams = true;
    logSso('INIT', '✅ Teams SDK initialized successfully - Running in Teams context');
    
    // Tell Teams the app's initial page content has loaded
    try {
      microsoftTeams.app.notifyAppLoaded();
      logSso('INIT', '✅ Notified Teams: app loaded');
    } catch (e) {
      logSso('INIT', '⚠️ notifyAppLoaded failed', { error: e.message });
    }
    
    // Get Teams context
    try {
      teamsContext = await microsoftTeams.app.getContext();
      logSso('CONTEXT', '✅ Teams context retrieved', {
        userPrincipalName: teamsContext?.user?.userPrincipalName,
        userObjectId: teamsContext?.user?.id,
        tenantId: teamsContext?.user?.tenant?.id,
        hostClientType: teamsContext?.app?.host?.clientType,
        frameContext: teamsContext?.page?.frameContext,
        locale: teamsContext?.app?.locale
      });
    } catch (e) {
      logSso('CONTEXT', '⚠️ Could not get Teams context', { error: e.message });
    }
    
    // Fast path: if we already have tokens, verify they're structurally valid JWTs
    // App.vue already validates against the backend — this is a quick format check only
    if (isAuthenticated()) {
      const token = localStorage.getItem('id_token');
      if (token && token.split('.').length === 3) {
        logSso('SSO', '✅ Already authenticated with valid JWT — skipping SSO token exchange');
        return true;
      }
      // Token exists but is malformed — clear it and proceed with SSO
      logSso('SSO', '⚠️ Existing token is malformed, clearing and doing fresh SSO');
      localStorage.removeItem('id_token');
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
    }
    
    // Try silent SSO authentication
    let ssoSuccess = await attemptTeamsSso();
    
    // Retry once after 1s if first attempt failed
    if (!ssoSuccess) {
      logSso('SSO', '🔄 First SSO attempt failed, retrying in 1s...');
      await new Promise(resolve => setTimeout(resolve, 1000));
      ssoSuccess = await attemptTeamsSso();
    }
    
    return ssoSuccess;
    
  } catch (error) {
    isInTeams = false;
    logSso('INIT', '❌ Teams SDK init failed - Running in browser context', { 
      error: error.message,
      stack: error.stack 
    });
    return false;
  }
};

/**
 * Attempt Teams SSO (Silent Sign-On)
 * @returns {Promise<boolean>} - true if SSO succeeded
 */
async function attemptTeamsSso() {
  logSso('SSO', 'Attempting Teams SSO...');
  
  try {
    // Determine scope URI
    const derivedScope = `api://${new URL(config.frontendUrl).hostname}/${config.clientId}`;
    const scopeUri = config.apiScopeUri || derivedScope;
    
    logSso('SSO', 'Requesting SSO token with scope', {
      configuredApiScopeUri: config.apiScopeUri,
      derivedScope: derivedScope,
      usingScopeUri: scopeUri,
      clientId: config.clientId,
      frontendUrl: config.frontendUrl
    });
    
    // Request SSO token from Teams (with 5s timeout to prevent hanging)
    let ssoToken;
    try {
      ssoToken = await Promise.race([
        microsoftTeams.authentication.getAuthToken({ resources: [scopeUri] }),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('SSO token request timed out after 5s')), 5000)
        )
      ]);
    } catch (tokenError) {
      logSso('SSO', '❌ getAuthToken failed', {
        error: tokenError.message,
        errorCode: tokenError.errorCode,
        stack: tokenError.stack
      });
      return false;
    }
    
    if (!ssoToken) {
      logSso('SSO', '❌ No SSO token received from Teams (token was null/undefined)');
      return false;
    }
    
    // Decode and log token info (without sensitive parts)
    try {
      const tokenParts = ssoToken.split('.');
      if (tokenParts.length === 3) {
        const payload = JSON.parse(atob(tokenParts[1]));
        logSso('SSO', '✅ Got SSO token from Teams', {
          aud: payload.aud,
          iss: payload.iss,
          name: payload.name,
          upn: payload.upn || payload.preferred_username,
          exp: new Date(payload.exp * 1000).toISOString(),
          tokenLength: ssoToken.length
        });
      }
    } catch (e) {
      logSso('SSO', '✅ Got SSO token (could not decode)', { tokenLength: ssoToken.length });
    }
    
    logSso('SSO', 'Exchanging SSO token via backend OBO flow...', {
      endpoint: `${config.apiUrl}/api/auth/teams-token`
    });
    
    // Exchange SSO token for Graph tokens via backend OBO flow
    const response = await fetch(`${config.apiUrl}/api/auth/teams-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ ssoToken })
    });
    
    if (!response.ok) {
      let errorData = {};
      try {
        errorData = await response.json();
      } catch (e) {
        errorData = { parseError: 'Could not parse error response' };
      }
      
      logSso('SSO', '❌ Backend token exchange failed', {
        status: response.status,
        statusText: response.statusText,
        error: errorData
      });
      
      // If consent required, fall back to interactive login
      if (errorData.error === 'consent_required') {
        logSso('SSO', '⚠️ Admin consent required - falling back to interactive login');
        return false;
      }
      
      return false;
    }
    
    const tokenData = await response.json();
    
    logSso('SSO', '✅ Backend token exchange successful', {
      hasAccessToken: !!tokenData.access_token,
      hasIdToken: !!tokenData.id_token,
      hasRefreshToken: !!tokenData.refresh_token,
      user: tokenData.user
    });
    
    // Store tokens
    if (tokenData.access_token) {
      localStorage.setItem('access_token', tokenData.access_token);
    }
    if (tokenData.id_token) {
      localStorage.setItem('id_token', tokenData.id_token);
    }
    if (tokenData.refresh_token) {
      localStorage.setItem('refresh_token', tokenData.refresh_token);
    }
    
    // Store user info
    if (tokenData.user) {
      localStorage.setItem('user_id', tokenData.user.id);
      localStorage.setItem('user_name', tokenData.user.name);
      localStorage.setItem('user_email', tokenData.user.email);
    }
    
    // Verify we actually got a usable id_token (required for authenticatedFetch)
    if (!tokenData.id_token) {
      logSso('SSO', '❌ OBO response missing id_token — SSO incomplete');
      return false;
    }
    
    logSso('SSO', '🎉 Teams SSO completed successfully!', { email: tokenData.user?.email });
    return true;
    
  } catch (error) {
    logSso('SSO', '❌ Teams SSO failed with exception', {
      error: error.message,
      stack: error.stack
    });
    return false;
  }
}

/**
 * Check if running in Teams
 */
export const getIsInTeams = () => isInTeams;

/**
 * Notify Teams that the app has fully loaded and is ready
 * Safe to call in both Teams and browser contexts
 */
export const notifyTeamsAppLoaded = () => {
  if (!isInTeams) return;
  try {
    microsoftTeams.app.notifySuccess();
  } catch (e) {
    console.warn('notifySuccess failed:', e.message);
  }
};

/**
 * Get Teams context
 */
export const getTeamsContext = () => teamsContext;

// OAuth2 configuration
export const oauth2Config = {
  auth: {
    clientId: config.clientId,
    authority: `https://login.microsoftonline.com/${config.tenantId}`,
    redirectUri: config.frontendUrl,
    postLogoutRedirectUri: config.frontendUrl,
  },
  cache: {
    cacheLocation: "localStorage",
    storeAuthStateInCookie: true // Important for IE/Edge and Teams
  },
  scopes: ['openid', 'profile', 'User.Read', 'email', 'offline_access']
};

/**
 * Initiates the OAuth2 login process with PKCE
 * Uses popup in Teams context, redirect in browser
 */
export const oauthLogin = async () => {
  try {
    // Validate config
    if (!oauth2Config.auth.clientId || !oauth2Config.auth.authority) {
      throw new Error('OAuth2 configuration incomplete');
    }

    // Generate and store PKCE values
    const codeVerifier = generateCodeVerifier();
    const state = generateStateChallenge();
    
    localStorage.setItem('pkce_code_verifier', codeVerifier);
    localStorage.setItem('oauth_state', state);

    // Generate Code Challenge
    const codeChallenge = await sha256(codeVerifier);

    // Use same redirect URI for browser and Teams
    const redirectUri = oauth2Config.auth.redirectUri;

    // Build Authorization URL
    const authParams = new URLSearchParams({
      client_id: oauth2Config.auth.clientId,
      response_type: 'code',
      redirect_uri: redirectUri,
      scope: oauth2Config.scopes.join(' '),
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
      state: state,
      prompt: 'select_account'
    });

    const authUrl = `${oauth2Config.auth.authority}/oauth2/v2.0/authorize?${authParams.toString()}`;

    if (isInTeams) {
      // In Teams: Use authentication popup
      microsoftTeams.authentication.authenticate({
        url: authUrl,
        width: 600,
        height: 535,
        successCallback: async (result) => {
          if (result) {
            localStorage.setItem('user_id', result.id);
            localStorage.setItem('user_name', result.displayName);
            localStorage.setItem('user_email', result.mail || result.userPrincipalName);
          }
          window.location.reload();
        },
        failureCallback: (reason) => {
          console.error('Teams auth failed:', reason);
        }
      });
    } else {
      // Browser: Regular redirect
      window.location.href = authUrl;
    }
  } catch (error) {
    console.error('OAuth login error:', error);
    throw error;
  }
};

/**
 * Processes the OAuth2 callback and exchanges the code for a token
 */
export const handleRedirectCallback = async () => {
  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get('code');
  const state = urlParams.get('state');
  const error = urlParams.get('error');
  const errorDescription = urlParams.get('error_description');

  // Check for errors
  if (error) {
    if (isInTeams) {
      microsoftTeams.authentication.notifyFailure(error);
    }
    throw new Error(`OAuth error: ${error} - ${errorDescription}`);
  }

  // Validate state
  const savedState = localStorage.getItem('oauth_state');
  if (state !== savedState) {
    if (isInTeams) {
      microsoftTeams.authentication.notifyFailure('Invalid state parameter');
    }
    throw new Error('Invalid state parameter');
  }

  if (code) {
    try {
      // Use same redirect URI for browser and Teams
      const redirectUri = oauth2Config.auth.redirectUri;

      // Fetch token
      const tokenResponse = await fetchToken(code, redirectUri);
      if (!tokenResponse) {
        throw new Error('No token response received');
      }

      // Store tokens
      localStorage.setItem('access_token', tokenResponse.access_token);
      if (tokenResponse.id_token) {
        localStorage.setItem('id_token', tokenResponse.id_token);
      }
      if (tokenResponse.refresh_token) {
        localStorage.setItem('refresh_token', tokenResponse.refresh_token);
      }

      // Fetch user info
      const userInfo = await fetchUserInfo(tokenResponse.access_token);
      if (!userInfo) {
        throw new Error('No user info received');
      }

      // Store user info
      localStorage.setItem('user_id', userInfo.id);
      localStorage.setItem('user_name', userInfo.displayName);
      localStorage.setItem('user_email', userInfo.mail || userInfo.userPrincipalName);

      // Cleanup
      localStorage.removeItem('pkce_code_verifier');
      localStorage.removeItem('oauth_state');

      // If in Teams popup, notify success and close the window
      if (isInTeams) {
        await microsoftTeams.app.initialize();
        microsoftTeams.authentication.notifySuccess(userInfo);
        return null; // Return null so popup closes
      }

      // Clean URL (remove ?code=... and ?state=... parameters)
      if (window.history && window.history.replaceState) {
        const cleanUrl = window.location.origin + window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
      }

      return userInfo;
    } catch (error) {
      if (isInTeams) {
        microsoftTeams.authentication.notifyFailure(error.message);
      }
      throw error;
    }
  }
  return null;
};

/**
 * Exchanges the authorization code for a token
 */
export const fetchToken = async (code, redirectUri = null) => {
  const tokenUrl = `${oauth2Config.auth.authority}/oauth2/v2.0/token`;
  const codeVerifier = localStorage.getItem('pkce_code_verifier');

  if (!codeVerifier) {
    throw new Error('No PKCE code verifier found');
  }

  // Use provided redirectUri or default
  const finalRedirectUri = redirectUri || oauth2Config.auth.redirectUri;

  const tokenData = new URLSearchParams({
    client_id: oauth2Config.auth.clientId,
    grant_type: 'authorization_code',
    code: code,
    redirect_uri: finalRedirectUri,
    code_verifier: codeVerifier,
    scope: oauth2Config.scopes.join(' ')
  });

  const response = await fetch(tokenUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'Accept': 'application/json'
    },
    body: tokenData.toString()
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(`Token request failed: ${errorData.error_description || response.statusText}`);
  }

  return await response.json();
};

/**
 * Fetches user info from Microsoft Graph API
 */
export const fetchUserInfo = async (accessToken) => {
  const response = await fetch('https://graph.microsoft.com/v1.0/me', {
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Accept': 'application/json'
    }
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(`Failed to fetch user info: ${errorData.error?.message || response.statusText}`);
  }

  return await response.json();
};

/**
 * Checks if the user is logged in
 */
export const isAuthenticated = () => {
  return !!localStorage.getItem('id_token');
};

/**
 * Returns stored user info
 */
export const getUserInfo = () => {
  if (!isAuthenticated()) {
    return null;
  }

  return {
    id: localStorage.getItem('user_id'),
    name: localStorage.getItem('user_name'),
    email: localStorage.getItem('user_email')
  };
};

/**
 * Clears auth data (shows login screen)
 */
export const clearAuthData = () => {
  const itemsToRemove = [
    'access_token',
    'id_token',
    'refresh_token',
    'user_id',
    'user_name',
    'user_email',
    'pkce_code_verifier',
    'oauth_state'
  ];

  itemsToRemove.forEach(item => localStorage.removeItem(item));
  
  window.dispatchEvent(new CustomEvent('authStateChanged', { 
    detail: { authenticated: false, reason: 'token_expired' } 
  }));
};

/**
 * Logout function
 */
export const logout = async () => {
  const itemsToRemove = [
    'access_token',
    'id_token',
    'refresh_token',
    'user_id',
    'user_name',
    'user_email',
    'pkce_code_verifier',
    'oauth_state'
  ];

  itemsToRemove.forEach(item => localStorage.removeItem(item));

  // Build the logout URL
  const logoutUrl = `${oauth2Config.auth.authority}/oauth2/v2.0/logout`;

  if (isInTeams) {
    // In Teams: Use authentication popup for logout
    microsoftTeams.authentication.authenticate({
      url: logoutUrl,
      width: 600,
      height: 535,
      successCallback: () => {
        // Clear auth data only (preserve user preferences like favorites, recent items)
        clearAuthData();
        window.location.reload();
      },
      failureCallback: (reason) => {
        // Even on failure, clear auth data and reload
        console.warn('Teams logout popup closed:', reason);
        clearAuthData();
        window.location.reload();
      }
    });
  } else {
    // Browser: Regular redirect with post_logout_redirect_uri
    window.location.href = `${logoutUrl}?post_logout_redirect_uri=${encodeURIComponent(oauth2Config.auth.postLogoutRedirectUri)}`;
  }
};

/**
 * Refreshes the access token using refresh token
 * Uses a mutex to prevent concurrent refresh attempts
 */
let refreshPromise = null;

export const refreshAccessToken = async () => {
  // If a refresh is already in progress, wait for it instead of starting another
  if (refreshPromise) {
    return refreshPromise;
  }
  
  refreshPromise = (async () => {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }
      
      const tokenUrl = `${oauth2Config.auth.authority}/oauth2/v2.0/token`;
      const tokenData = new URLSearchParams({
        client_id: oauth2Config.auth.clientId,
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
        scope: oauth2Config.scopes.join(' ')
      });

      const response = await fetch(tokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: tokenData.toString()
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const tokenResponse = await response.json();
      
      localStorage.setItem('access_token', tokenResponse.access_token);
      if (tokenResponse.id_token) {
        localStorage.setItem('id_token', tokenResponse.id_token);
      }
      if (tokenResponse.refresh_token) {
        localStorage.setItem('refresh_token', tokenResponse.refresh_token);
      }

      return tokenResponse;
    } catch (refreshError) {
      // In Teams: standard refresh fails because the refresh_token was obtained
      // via OBO (confidential client) and can't be used without client_secret.
      // Fall back to Teams SSO to get fresh tokens transparently.
      if (isInTeams) {
        console.log('[Token] Standard refresh failed in Teams, attempting SSO renewal...');
        const ssoSuccess = await attemptTeamsSso();
        if (ssoSuccess) {
          console.log('[Token] SSO renewal succeeded — tokens refreshed seamlessly');
          return { renewed_via: 'teams_sso' };
        }
      }
      throw refreshError;
    } finally {
      refreshPromise = null;
    }
  })();
  
  return refreshPromise;
};

/**
 * Checks if ID token is expired or expiring soon (< 10 minutes)
 * and refreshes automatically if needed
 */
export const ensureValidToken = async () => {
  const idToken = localStorage.getItem('id_token');
  
  if (!idToken) {
    clearAuthData();
    return;
  }

  try {
    const parts = idToken.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid ID token format');
    }

    // JWTs use base64url encoding (- and _ instead of + and /), not standard base64.
    // atob() throws InvalidCharacterError on base64url chars → must convert before decoding.
    const base64Url = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64Url.padEnd(base64Url.length + (4 - base64Url.length % 4) % 4, '=');
    const payload = JSON.parse(atob(padded));
    const expiresAt = payload.exp * 1000;
    const now = Date.now();
    const tenMinutes = 10 * 60 * 1000;

    // Token expired
    if (expiresAt <= now) {
      try {
        await refreshAccessToken();
        return;
      } catch (refreshError) {
        clearAuthData();
        return;
      }
    }
    
    // Token expiring in < 10 minutes
    if (expiresAt - now < tenMinutes) {
      try {
        await refreshAccessToken();
      } catch (refreshError) {
        console.warn('Token refresh failed, but token still valid');
      }
    }
  } catch (error) {
    try {
      await refreshAccessToken();
    } catch (refreshError) {
      clearAuthData();
    }
  }
};

/**
 * Wrapper for fetch() with automatic token validation and retry logic
 */
export const authenticatedFetch = async (url, options = {}) => {
  const idToken = localStorage.getItem('id_token');
  
  if (!idToken) {
    throw new Error('Not authenticated - no valid token found');
  }
  
  // Validate and refresh if needed
  await ensureValidToken();
  const authToken = localStorage.getItem('id_token');
  
  // Merge headers with Authorization (only add Content-Type if not already set)
  const headers = {
    ...(options.headers?.['Content-Type'] ? {} : { 'Content-Type': 'application/json' }),
    ...options.headers,
    'Authorization': `Bearer ${authToken}`,
  };

  try {
    const response = await fetch(url, {
      ...options,
      headers
    });

    // If 401, try to refresh and retry
    if (response.status === 401) {
      try {
        await refreshAccessToken();
        const retryToken = localStorage.getItem('id_token');
        
        if (!retryToken) {
          throw new Error('No token after refresh');
        }
        
        const retryHeaders = {
          ...(options.headers?.['Content-Type'] ? {} : { 'Content-Type': 'application/json' }),
          ...options.headers,
          'Authorization': `Bearer ${retryToken}`,
        };
        
        const retryResponse = await fetch(url, {
          ...options,
          headers: retryHeaders
        });

        return retryResponse;
      } catch (refreshError) {
        clearAuthData();
        throw new Error('Authentication failed - please login again');
      }
    }

    return response;
  } catch (error) {
    throw error;
  }
};
